import { getAuth } from "firebase/auth";
import { getReadyAuthUser } from "~/utils/auth/waitForAuthReady";
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
} from "react-router-dom";
import { INTENTS, loadCookiesData } from "~/utils";
import { withEntityCacheInvalidation } from "~/utils/loaderQueryCache";
import * as APITypes from "~/API";
import { apiClient, AddAmenityPageLoaderResult } from "~/services/apiClient";

export const AddAmenityPageLoader = async ({
  request,
  params,
}: LoaderFunctionArgs): Promise<AddAmenityPageLoaderType | Response> => {
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user?.uid || "");
  if (!authAccessInfo.success) redirect("/explore");
  const { ownerID, ownerType, role } = authAccessInfo.data;

  const propertyID = params.propertyID || "";
  if (!propertyID) return redirect("/property");

  const isAdminOrSuperAdmin =
    role === APITypes.ManagerRolesType.SuperAdmin ||
    role === APITypes.ManagerRolesType.Admin;

  const result = await apiClient.loader<AddAmenityPageLoaderResult>(
    "add-amenity-page",
    {
      propertyID,
      ownerID,
      ownerType,
      userID: user.uid,
    }
  );

  if (!result.success || !result.data) {
    console.error("Failed to load add amenity page:", result.error);
    throw new Error(
      result.error ?? "Failed to load add amenity page"
    );
  }

  const data = result.data;
  const canCreateAmenity = isAdminOrSuperAdmin || data.canCreateAmenity;

  return {
    buildingList: data.buildingList,
    property: data.property,
    canCreateAmenity,
  };
};

export type AddAmenityPageLoaderType = {
  buildingList: APITypes.Building[];
  property: APITypes.Property;
  canCreateAmenity: boolean;
};

export const AddAmenityPageAction = async ({
  request,
  params,
}: ActionFunctionArgs) => {
  const formData = await request.formData();
  const intent = formData.get("intent");
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user.uid);
  if (!authAccessInfo.success || !authAccessInfo.data) return redirect("/explore");
  const { role } = authAccessInfo.data;
  const propertyID = params.propertyID || "";

  if (propertyID === "" || intent !== INTENTS._add_amenity) {
    return {};
  }

  const rawBody = formData.get("body") as string | null;
  if (!rawBody) return {};
  let formValues: Record<string, unknown> = {};
  try {
    formValues = JSON.parse(rawBody) as Record<string, unknown>;
  } catch {
    return {};
  }

  const isAdminOrSuperAdmin =
    role === APITypes.ManagerRolesType.SuperAdmin ||
    role === APITypes.ManagerRolesType.Admin;

  const result = await withEntityCacheInvalidation(
    [{ entity: "amenity" }],
    async () =>
      apiClient.sendIntent<{ id?: string }>(INTENTS._add_amenity, {
        ...formValues,
        propertyID,
        isAdminOrSuperAdmin,
      }),
  );

  if (!result.success || !result.data) {
    console.error("Lambda _add_amenity failed", result.error);
    return { success: false, error: result.error ?? "Failed to create amenity" };
  }

  const createdId = result.data?.id;
  if (createdId) {
    return redirect(`/property/${propertyID}/amenity/${createdId}`);
  }
  return {};
};
